import React from "react";
import { Box, FormControl, Grid, Typography } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { getPasswordResetVerifySchema, type PasswordResetVerifyData } from "../../../schemas";
import { PasswordResetInput, ApplicationWordArt, AuthenticationButton } from "../../../components";

export interface PasswordResetVerifyFormProps {
	readonly onSubmit: (data: PasswordResetVerifyData) => void;
	readonly username: string;
	readonly errorMessage: string | null;
}

export const PasswordResetVerifyForm: React.FC<PasswordResetVerifyFormProps> = ({
	onSubmit,
	username,
	errorMessage,
}) => {
	const intl = useIntl();

	const {
		handleSubmit,
		formState: { errors, isValid, isSubmitting },
		control,
	} = useForm<PasswordResetVerifyData>({
		resolver: zodResolver(getPasswordResetVerifySchema(intl)),
		defaultValues: {
			email: username,
			token: "",
		},
	});

	return (
		<Grid container component="form" onSubmit={handleSubmit(onSubmit)} direction="column" gap={6}>
			<Box sx={{ margin: "auto" }}>
				<ApplicationWordArt width={300} />
			</Box>

			<Grid container direction="column" alignItems="center">
				<Typography variant="h1" color="white">
					<FormattedMessage id="password-reset-verify.title" defaultMessage="Verify your email" />
				</Typography>
			</Grid>

			<Grid container direction="column" gap={2}>
				<Typography color="white" variant="body2" data-testid="password-reset-verify__text__instructions">
					<FormattedMessage
						id="password-reset-verify.instructions"
						defaultMessage="If an account exists for {email}, you will get an email with a password reset code."
						values={{ email: username }}
					/>
				</Typography>

				<FormControl error={Boolean(errors.token)}>
					<Controller
						name="token"
						control={control}
						render={({ field }) => (
							<PasswordResetInput
								onChange={field.onChange}
								error={Boolean(errorMessage) || Boolean(errors.token)}
								helperText={errorMessage || errors.token?.message}
							/>
						)}
					/>
				</FormControl>

				<Grid container marginBottom={2} direction="column">
					<AuthenticationButton
						data-testid="password-reset-verify__button__submit"
						variant="contained"
						disabled={!isValid}
						loading={isSubmitting}
						type="submit"
					>
						<FormattedMessage id="password-reset-verify.submit" defaultMessage="Next" />
					</AuthenticationButton>
				</Grid>
			</Grid>
		</Grid>
	);
};
